<script lang="ts">
    import { useFragment } from '$gql/fragment-masking.js';
    import {
        FeedParticipantFragmentDoc,
        LinkMetaDataFragmentDoc,
        MediaImageFragmentDoc,
        type NewsFeedPostFragment,
        ParticipantAvatarFragmentDoc,
        ParticipantCardFragmentDoc,
        RelatedFeedPostFragmentDoc,
        ViewMetaFragmentDoc,
    } from '$gql/graphql.js';
    import FeedPostMenu from './FeedPostMenu.svelte';
    import ParticipantAvatar from '../ParticipantAvatar/ParticipantAvatar.svelte';
    import { type CommentData, FEED_POST_BODY_MAX_LENGTH } from './Utils';
    import filter from 'lodash/filter';
    import map from 'lodash/map';
    import { getContainer } from '$components/Context/ContainerContext.js';
    import LinkMetaData from '$components/LinkMetaData/LinkMetaData.svelte';
    import FeedPostMediaCollection from './FeedPostMediaCollection.svelte';
    import PostEventInfo from './PostEventInfo.svelte';
    import PostTag from './PostTag.svelte';
    import RelatedFeedPost from './RelatedFeedPost.svelte';
    import truncate from 'lodash/truncate';
    import MentionText from './MentionText.svelte';
    import FeedPostFooter from './FeedPostFooter.svelte';
    import EditFeedPostModal, { createModal } from './EditFeedPostModal.svelte';
    import ParticipantCard from '$components/ParticipantCard/ParticipantCard.svelte';
    import ModalGoafricaContact from '$components/GoafricaContact/ModalGoafricaContact.svelte';
    import ViewMeta from './ViewMeta.svelte';
    import { cx } from '$lib/utils/cx.js';
    import {
        fromZonedDateTimeStringToLocalDate,
        fromZonedDateTimeStringToLocalDateOrNull,
    } from '$lib/utils/zonedDateTime';
    import { isAfter } from 'date-fns';
    import { nullIfEmptyString } from '$lib/utils/nullIfEmptyString';
    import * as t from '$trans';
    import { inview } from 'svelte-inview';
    import { differenceInMilliseconds } from 'date-fns/differenceInMilliseconds';
    import PostCategoryInfo from './CategoryFeed/PostCategoryInfo.svelte';
    import {
        createForm,
        partialFromCurrentUser,
    } from '$components/GoafricaContact/GoafricaContactFormData';
    import uniqueId from 'lodash/uniqueId';

    export let post: NewsFeedPostFragment;
    export let withAnalytics: boolean = true;

    let className: string = '';
    export { className as class };

    export let commentData: CommentData | null = null;

    const { intl, datalayer, lang, currentUserStore } = getContainer();
    let currentUser = currentUserStore().currentUser();

    let fullTextMode: boolean = false;
    let deleting: boolean = false;

    const modalEdit = createModal({ defaultOpen: false });
    const modalReport = createModal({ defaultOpen: false });
    const [formReport, fieldsReport] = createForm(
        `${uniqueId(`goafrica-contact-feedpost-${post.id}`)}`,
        {
            subject: 'report_feed_post',
            to: 'administration',
            relatedFeedPostId: post.id,
            ...partialFromCurrentUser($currentUser),
        },
        {
            withCompanyNameRequired: false,
            withPhoneRequired: false,
            withSubject: false,
        },
    );

    $: formReport.dispatch(
        formReport.actions().changeValue(post.id, fieldsReport.relatedFeedPostId),
    );

    $: author = useFragment(FeedParticipantFragmentDoc, post.author);
    $: linkMetaDatas = map(post.linkMetaDatas, (l) =>
        useFragment(LinkMetaDataFragmentDoc, l),
    );
    $: relatedFeedPost = useFragment(RelatedFeedPostFragmentDoc, post.relatedFeedPost);
    $: medias = map(post.medias, (m) => useFragment(MediaImageFragmentDoc, m));
    $: viewMeta = useFragment(ViewMetaFragmentDoc, post.viewMeta);

    function getText(text: string, showAll: boolean) {
        if (text == null) return;
        if (showAll) {
            return text;
        } else {
            return truncate(text, { length: FEED_POST_BODY_MAX_LENGTH });
        }
    }

    $: text = getText(post?.body, fullTextMode);
    $: pendingPublicationDate =
        fromZonedDateTimeStringToLocalDateOrNull(
            post.pendingPublicationDate ?? null,
        )?.toDate() ?? null;
    $: isStillPending =
        pendingPublicationDate == null
            ? false
            : isAfter(pendingPublicationDate, new Date());

    $: authorTitle =
        author.__typename === 'User'
            ? author.participantTitle
            : `${intl().countryLabel(author.country)}${
                  author.country !== null ? ' > ' : ''
              }${author.category.category}`;

    let inviewEnterDate: Date | null = null;

    function inViewEnter() {
        inviewEnterDate = inviewEnterDate ?? new Date();
        if (withAnalytics) {
            datalayer()?.push({
                event: 'feed_post.in_view.enter',
                feed_post: {
                    id: post.id,
                    tag: post.tag,
                    hasMedias: post.medias.length > 0,
                    createdDate: fromZonedDateTimeStringToLocalDate(post.createdDate)
                        .toDate()
                        .toISOString(),
                },
            });
        }
    }

    function inViewLeave() {
        const viewingTimeInMs = differenceInMilliseconds(
            new Date(),
            inviewEnterDate ?? new Date(),
        );
        inviewEnterDate = null;

        if (withAnalytics && viewingTimeInMs > 0) {
            datalayer()?.push({
                event: 'feed_post.in_view.leave',
                feed_post: {
                    id: post.id,
                    tag: post.tag,
                    hasMedias: post.medias.length > 0,
                    createdDate: fromZonedDateTimeStringToLocalDate(post.createdDate)
                        .toDate()
                        .toISOString(),
                },
                viewing_time_in_ms: viewingTimeInMs,
            });
        }
    }

    function onVisibilityChange() {
        if (document.visibilityState === 'hidden') {
            inViewLeave();
        }
    }
</script>

<svelte:document on:visibilitychange={onVisibilityChange} />

<div
    use:inview={{}}
    on:inview_enter={inViewEnter}
    on:inview_leave={inViewLeave}
    class="w-full"
>
    <div
        class={cx('p-[12px] pb-0 rounded-[16px] bg-white shadow-leo', className)}
        class:loading={deleting}
    >
        {#if viewMeta != null}
            <ViewMeta {viewMeta} />
        {/if}

        <div class="grid-header mb-8">
            <ParticipantCard
                class="[grid-area:avatar]"
                participant={useFragment(ParticipantCardFragmentDoc, author)}
            >
                <ParticipantAvatar
                    participant={useFragment(ParticipantAvatarFragmentDoc, author)}
                />
            </ParticipantCard>

            <ParticipantCard
                class="[grid-area:name] inline truncate"
                participant={useFragment(ParticipantCardFragmentDoc, author)}
            >
                <a
                    href={author.participantProfileLink}
                    data-sveltekit-reload
                    class="text-12 font-bold truncate"
                >
                    {author.participantName}
                </a>
            </ParticipantCard>

            {#if nullIfEmptyString(authorTitle) !== null}
                <div class={'[grid-area:title] italic text-12 text-gray-600 truncate'}>
                    {authorTitle}
                </div>
            {/if}

            <a
                href={post.url}
                data-sveltekit-reload={false}
                class="[grid-area:date] italic text-12 text-gray-700 hover:text-gray-700 no-underline hover:underline cursor-pointer"
            >
                {#if pendingPublicationDate != null && isStillPending}
                    Programmé le {intl().formatDate(pendingPublicationDate, 'PP à p')}
                {:else}
                    {intl().formatDistance(
                        pendingPublicationDate ??
                            fromZonedDateTimeStringToLocalDate(post.createdDate).toDate(),
                        new Date(),
                        {
                            addSuffix: true,
                        },
                    )}
                {/if}
            </a>

            {#if post.tag != null}
                <div class={'[grid-area:tag]'}>
                    <PostTag tag={post.tag} />
                </div>
            {/if}

            <FeedPostMenu
                {post}
                className="[grid-area:menu]"
                onEdit={() => modalEdit.states.open.set(true)}
                onReport={() => modalReport.states.open.set(true)}
            />
        </div>

        {#if post.title}
            <h4>{post.title}</h4>
        {/if}
        <div class="text-14 mb-4 font-emoji break-words">
            <MentionText {text} skipMultiLineBreaks />
            {#if post.body.length > FEED_POST_BODY_MAX_LENGTH}
                <button
                    on:click|preventDefault={() => (fullTextMode = !fullTextMode)}
                    class={'btn-reset block w-full text-right text-link cursor-pointer hover:text-link-hover hover:underline'}
                >
                    {fullTextMode
                        ? t.feed_post.see_less({}, $lang)
                        : t.feed_post.see_more({}, $lang)}
                </button>
            {/if}
        </div>
        <FeedPostMediaCollection {medias} />

        {#if medias.length === 0}
            {#each filter(linkMetaDatas, (linkMetaData) => linkMetaData.relatedFeedPost === null) as linkMetaData (linkMetaData.id)}
                <LinkMetaData {linkMetaData} />
            {/each}
        {/if}

        {#if relatedFeedPost != null}
            <RelatedFeedPost post={relatedFeedPost} />
        {/if}

        <PostEventInfo {post} />
        <PostCategoryInfo {post} />
        <FeedPostFooter {post} {commentData} />
    </div>

    <EditFeedPostModal modal={modalEdit} {post} />

    <ModalGoafricaContact
        form={formReport}
        fields={fieldsReport}
        modal={modalReport}
        prefixId={`${uniqueId(`goafrica-contact-feed-form-${post.id}`)}`}
        currentUser={$currentUser}
        messageLabel={'Raison du signalement'}
        modalTitle={'Signaler un poste'}
        withCompanyNameRequired={false}
        withPhoneRequired={false}
        onSuccess={() => {
            modalReport.states.open.set(false);
        }}
    />
</div>

<style>
    .grid-header {
        display: grid;
        grid-template:
            'avatar         name    tag             menu' auto
            'avatar         title   tag             menu' auto
            'avatar         date    tag             menu' auto
            / max-content 1fr max-content max-content;

        column-gap: 12px;
        row-gap: 0;
    }
</style>
